/**
 * World generation: content + seed + a character sheet → a `WorldState`.
 *
 * Same seed, same district, every time (§19). Every draw goes through a
 * named fork of the world's `Rng`, so adding a draw in one place never
 * reshuffles another. The steps:
 *
 *  1. Brand the content cards into sim types (this is the one place ids
 *     are stamped, per `sim/types/ids.ts`).
 *  2. Apply the sheet: stats, flaws, starting relationships, money, pins.
 *  3. Seed what the spec says is seeded and V0 needs: the district name
 *     (§2) and the opening problem (§6).
 *
 * Not here, on purpose: the vanished crew and dormant situations (§3, V1).
 * `hook` stays null. The canon loads (`sim/canon.ts`) and is not consulted.
 */

import { answerGuided, type CharacterSheet, type GuidedAnswers, rollCharacter } from './chargen.js';
import { type Content, type JobCard, type LocationCard, loadContent, type NpcCard, parseClock } from './content.js';
import { footprintOf } from './geometry.js';
import { writeFact } from '../engine/memory/facts.js';
import { generateOpening } from './opening.js';
import { readConfidence } from './reducer.js';
import { Rng, type Seedable } from './rng.js';
import { addMinutes, timeOf } from './time.js';
import type {
  Job,
  JobId,
  Location,
  LocationId,
  NPC,
  NpcId,
  PlayerCharacter,
  RelationshipAxes,
  Secret,
  SecretId,
  Situation,
  SituationId,
  Stance,
  WorldState,
  WorldTime,
} from './types/index.js';
import { createWorld } from './world.js';

const npcId = (s: string): NpcId => s as NpcId;
const locationId = (s: string): LocationId => s as LocationId;
const jobId = (s: string): JobId => s as JobId;
const secretId = (s: string): SecretId => s as SecretId;

/** How well you already know the people you grew up around. Minutes. */
const HOMETOWN_EXPOSURE = 120;
/** How long before the cold open you last ate and slept. */
const HOURS_SINCE_ATE = 7;
const HOURS_SINCE_SLEPT = 15;

function toLocation(card: LocationCard, start: WorldTime): Location {
  return {
    id: locationId(card.id),
    name: card.name,
    kind: card.kind,
    owner: card.owner === null ? null : npcId(card.owner),
    entrance: card.entrance,
    phone: card.phone,
    lastResolved: start,
  };
}

/** The map's building and filler shapes, parsed once into `WorldState['footprints']`. */
function toFootprints(content: Content): WorldState['footprints'] {
  return {
    buildings: content.map.buildings.map((b) => ({ location: locationId(b.location), ...footprintOf(b.path) })),
    filler: content.map.filler.map((path) => footprintOf(path)),
  };
}

function toJob(card: JobCard): Job {
  return {
    id: jobId(card.id),
    name: card.name,
    kind: card.kind,
    employer: npcId(card.employer),
    at: locationId(card.at),
    days: card.days,
    window: { from: parseClock(card.window.from), to: parseClock(card.window.to) },
    minutes: card.minutes,
    pay: card.pay,
    requires: card.requires,
  };
}

function toNpc(card: NpcCard, start: WorldTime, known: Partial<RelationshipAxes> | undefined, isDependent: boolean): NPC {
  // The person you can't say no to starts where their card says (§5),
  // plus whatever the sheet added.
  const given = isDependent ? (card.dependent?.relationship ?? {}) : {};
  const relationship: RelationshipAxes = {
    trust: (given.trust ?? 0) + (known?.trust ?? 0),
    respect: (given.respect ?? 0) + (known?.respect ?? 0),
    fear: (given.fear ?? 0) + (known?.fear ?? 0),
    warmth: (given.warmth ?? 0) + (known?.warmth ?? 0),
  };
  const stances: Stance[] = card.stances.map((s) => ({ subject: { kind: 'npc', id: npcId(s.npc) }, disposition: s.disposition }));
  const acquainted = known !== undefined || isDependent;
  const exposure = acquainted ? HOMETOWN_EXPOSURE : 0;
  const base: Omit<NPC, 'read'> = {
    id: npcId(card.id),
    name: card.name,
    role: card.role,
    age: card.age,
    faction: null,
    promotedFrom: null,
    voice: card.voice,
    deceptionCapacity: card.deceptionCapacity,
    stats: card.stats,
    flaw: card.flaw,
    wants: card.wants,
    bonds: card.bonds.map(npcId),
    recruitment: card.recruitment,
    routine: card.routine.map((r) => ({ days: r.days, from: parseClock(r.from), to: parseClock(r.to), at: locationId(r.at) })),
    reachableAt: card.reachableAt.map(locationId),
    status: 'present',
    injuries: [],
    lastResolved: start,
    truth: {
      relationship,
      stances,
      loyalties: [],
      mood: card.mood,
      money: card.money,
      possessions: [],
      secrets: [secretId(card.secret.id)],
      perceivedPlayerStats: {},
    },
  };
  return {
    ...base,
    read: {
      // What you already know about people you grew up around is the
      // truth as of the day you left. What they've become since is not.
      relationship: acquainted ? relationship : { trust: 0, respect: 0, fear: 0, warmth: 0 },
      stances: [],
      confidence: readConfidence(base as NPC, exposure),
      exposure,
      acquaintance: acquainted ? 'known' : 'seen',
      lastSeen: null,
    },
  };
}

export interface GeneratedWorld {
  readonly state: WorldState;
  readonly sheet: CharacterSheet;
  /** The cold open, ready to show (§6). Also on `state.opening` minus the prose. */
  readonly openingLines: readonly string[];
}

/** Build a world from a finished sheet. */
export function generateWorld(seed: Seedable, sheet: CharacterSheet, content: Content = loadContent()): GeneratedWorld {
  const rng = new Rng(seed);
  const { manifest } = content;
  const start: WorldTime = timeOf(manifest.start.day, 0, parseClock(manifest.start.time));

  const districtName = rng.fork('district').pick(manifest.districtNames);
  const locations = content.locations.map((c) => toLocation(c, start));
  const jobs = content.jobs.map(toJob);
  const npcs = content.npcs.map((c) => toNpc(c, start, sheet.relationships[c.id], c.id === sheet.dependent));
  const secrets: Secret[] = content.npcs.map((c) => ({
    id: secretId(c.secret.id),
    owner: npcId(c.id),
    content: c.secret.content,
    knownBy: [npcId(c.id)],
    consequencesIfExposed: c.secret.consequencesIfExposed,
    createdOn: addMinutes(start, -30 * 24 * 60),
  }));
  for (const s of sheet.secrets) {
    secrets.push({
      id: secretId(s.id),
      owner: 'player',
      content: s.content,
      knownBy: ['player'],
      consequencesIfExposed: s.consequencesIfExposed,
      createdOn: start,
    });
  }

  const known = new Set<string>([...manifest.block.knownAtStart, ...sheet.knownLocations]);
  const startAt = locations.find((l) => l.id === manifest.block.startAt)!;
  const player: PlayerCharacter = {
    name: sheet.name,
    origin: { path: sheet.path, summary: sheet.summary },
    stats: sheet.stats,
    flaws: sheet.flaws,
    dependent: npcId(sheet.dependent),
    money: sheet.money,
    possessions: [],
    needs: {
      lastAte: addMinutes(start, -HOURS_SINCE_ATE * 60),
      lastSlept: addMinutes(start, -HOURS_SINCE_SLEPT * 60),
    },
    lodging: locationId(manifest.block.lodging),
    position: { point: startAt.entrance, at: startAt.id, debt: 0 },
    knownLocations: [...known].map(locationId),
    secrets: sheet.secrets.map((s) => secretId(s.id)),
    injuries: [],
    factionStanding: {},
    deathMode: 'standard',
    status: 'alive',
  };

  let state = createWorld({
    seed,
    districtName,
    bounds: content.map.bounds,
    footprints: toFootprints(content),
    start,
    player,
    npcs,
    locations,
    factions: [],
    secrets,
    jobs,
  });

  // Debts the character arrived with (the flaw budget's "someone's owed").
  for (const o of sheet.obligations) {
    state = writeFact(
      state,
      { kind: 'obligation', from: 'player', to: npcId(o.to), substance: { kind: 'money', amount: o.amount }, deadline: timeOf(start.day + o.deadlineDays, 18), settled: false },
      null,
    ).state;
  }

  // The cold open (§6).
  const opening = generateOpening(content, rng.fork('opening'), sheet.dependent, sheet.money, jobs, start);
  const debt = writeFact(
    state,
    {
      kind: 'obligation',
      from: npcId(opening.template.dependent),
      to: npcId(opening.template.owedTo),
      substance: { kind: 'money', amount: opening.amount },
      deadline: opening.deadline,
      settled: false,
    },
    null,
  );
  state = debt.state;
  const situationId = `situation_${opening.template.id}` as SituationId;
  const situation: Situation = {
    id: situationId,
    kind: 'opening_problem',
    involves: [{ kind: 'player' }, { kind: 'npc', id: npcId(opening.template.dependent) }, { kind: 'npc', id: npcId(opening.template.owedTo) }],
    state: 'surfaced',
    since: start,
  };
  state = {
    ...state,
    situations: { ...state.situations, [situationId]: situation },
    opening: {
      id: opening.template.id,
      dependent: npcId(opening.template.dependent),
      owedTo: npcId(opening.template.owedTo),
      need: opening.template.need,
      amount: opening.amount,
      deadline: opening.deadline,
      solvability: opening.solvability,
      obligation: debt.fact.id,
      situation: situationId,
      failureOpens: npcId(opening.template.failureOpens),
    },
  };

  return { state, sheet, openingLines: opening.lines };
}

export type CharacterChoice =
  | { readonly path: 'random'; readonly roll?: number }
  | { readonly path: 'guided'; readonly answers: GuidedAnswers; readonly name?: string };

/** The whole thing: a seed and a way of making a character, to a world. */
export function newWorld(seed: Seedable, choice: CharacterChoice, content: Content = loadContent()): GeneratedWorld {
  const sheet =
    choice.path === 'random'
      ? rollCharacter(content, seed, choice.roll ?? 0)
      : answerGuided(content, seed, choice.answers, choice.name);
  return generateWorld(seed, sheet, content);
}
